import { get } from 'lodash';
import constants from 'src/constants';
import {
  ProductCatalogProduct,
  ProductCatalogGrouping,
  ProductCatalogVariation,
} from 'src/generated/graphql';

const {
  DISCONTINUED_GAF_VARIATION_GROUPS,
  DISCONTINUED_CERTAINTEED_VARIATION_GROUPS,
  DISCONTINUED_CERTAINTEED_VARIATIONS,
  DISCONTINUED_PLYGEM_PRODUCTS,
  DISCONTINUED_MASTIC_PRODUCTS,
  DISCONTINUED_MASTIC_VARIATIONS,
  PAINTABLE_BRICK_PRODUCT,
  PAINTABLE_BRICK_PROVIDER,
} = constants.productCatalog;

const { PAINTABLE_MATERIALS } = constants.labels;

const isHoverGenericMetalRoof = (product: any = {}): boolean => {
  return product.id === 9951 && product.name === 'Metal Roof';
};

const isDiscontinuedGAFVariationGroup = (item: any = {}): boolean => {
  return !!DISCONTINUED_GAF_VARIATION_GROUPS[item.id];
};

const isDiscontinuedCertainTeedVariationGroup = (item: any = {}): boolean => {
  return !!DISCONTINUED_CERTAINTEED_VARIATION_GROUPS[item.id];
};

const isDiscontinuedCertainTeedVariation = (variation: any = {}): boolean => {
  return !!DISCONTINUED_CERTAINTEED_VARIATIONS[variation.id];
};

const isDiscontinuedMasticProduct = (product: any = {}): boolean => {
  return !!DISCONTINUED_MASTIC_PRODUCTS[product.id];
};

const isDiscontinuedMasticVariation = (variation: any = {}): boolean => {
  return !!DISCONTINUED_MASTIC_VARIATIONS[variation.id];
};

const isDiscontinuedPlygemProduct = (product: any = {}): boolean => {
  return !!DISCONTINUED_PLYGEM_PRODUCTS[product.id];
};

/*
 * @param {number} productId
 * @return {boolean}
 */
const isPaintableBrickProduct = (productId: number): boolean => {
  return productId === PAINTABLE_BRICK_PRODUCT.legacy_id;
};

/*
 * @param {number} materialId - this is the label id of the material
 * @return {boolean} - return true if the material is paintable
 */
const isPaintableMaterial = (materialId: number): boolean => {
  return PAINTABLE_MATERIALS.includes(materialId);
};

const isPaintableBrickProvider = (provider: any = {}): boolean => {
  return provider.id === PAINTABLE_BRICK_PROVIDER.id;
};

const doesBrandProvideMaterial = (
  manufacturer: any,
  materialId: number,
): boolean => {
  const { label_ids = new Set() } = manufacturer;
  return !!label_ids.has?.(materialId);
};

interface Record {
  id: number;
}

const getDefaultTextureForLabel = ({ id }: Record, products: any): any =>
  products.find(
    (p: any) =>
      p.label_id === id &&
      get(p, 'texture.thumbnail.url') &&
      p.default === true,
  );

const convertManufacturerProductsResponseData = (
  responseData: ProductCatalogProduct[],
) => {
  const groupingAttributeExtractor = (
    product: ProductCatalogProduct,
    grouping: ProductCatalogGrouping,
  ) => {
    const { variations } = grouping;
    return {
      id: grouping.legacyId,
      name: grouping.name,
      parent_id: product.legacyId,
      deleted: null,
      manufacturer_id: product.provider && product.provider.legacyId,
      label_id: null,
      color: null,
      price: null,
      sort_order: null,
      paintable: false,
      product_type: null,
      children: variations.map((variation: ProductCatalogVariation) =>
        variationAttributeExtractor(product, variation, grouping),
      ),
    };
  };

  const variationAttributeExtractor = (
    product: ProductCatalogProduct,
    variation: ProductCatalogVariation,
    grouping?: ProductCatalogGrouping,
  ) => {
    const { productType = '' } = product;
    return {
      id: variation.legacyId,
      name: variation.name,
      manufacturer_id: product.provider && product.provider.legacyId,
      label_id: product.labelId,
      active: product.active,
      product_type:
        productType!.charAt(0).toUpperCase() +
        productType!.slice(1).toLowerCase(),
      product_id: variation.texture && variation.texture.legacyId,
      color: variation.color && variation.color.hex,
      parent_id: grouping ? grouping.legacyId : product.legacyId,
      paintable: true,
      children: [],
    };
  };

  const extractGroupingChildren = (product: ProductCatalogProduct) => {
    const { groupings } = product;
    return groupings!.map((grouping: ProductCatalogGrouping) =>
      groupingAttributeExtractor(product, grouping),
    );
  };

  const extractVariationChildren = (product: ProductCatalogProduct) => {
    const { ungroupedVariations } = product;
    return ungroupedVariations.map((variation: ProductCatalogVariation) =>
      variationAttributeExtractor(product, variation),
    );
  };

  return responseData.map((product: ProductCatalogProduct) => {
    const { ungroupedVariations } = product;
    const children = ungroupedVariations
      ? extractVariationChildren(product)
      : extractGroupingChildren(product);

    return {
      id: product.legacyId,
      name: product.name,
      manufacturer_id: product.provider && product.provider.legacyId,
      paintable: product.isPaintable,
      color: '',
      deleted: false,
      parent_id: null,
      // product_id paint hacks, see below for Legacy REST format:
      // https://github.com/hoverinc/product-catalog/blob/master/app/controllers/hover_models/manufacturer_products_controller.rb#L41
      product_id: !product.productType ? 142 : null,
      price: 0,
      sort_order: 0,
      children,
    };
  });
};

export default {
  convertManufacturerProductsResponseData,
  doesBrandProvideMaterial,
  getDefaultTextureForLabel,
  isDiscontinuedCertainTeedVariation,
  isDiscontinuedCertainTeedVariationGroup,
  isDiscontinuedGAFVariationGroup,
  isDiscontinuedPlygemProduct,
  isDiscontinuedMasticProduct,
  isDiscontinuedMasticVariation,
  isHoverGenericMetalRoof,
  isPaintableBrickProduct,
  isPaintableBrickProvider,
  isPaintableMaterial,
};
